import { sha256 } from "js-sha256";

import { getSegmentAnonymousId } from "@/components/vendor/Analytics";
import { EventData } from "@/types/common";
import { clearRemovedItem, clearCartUndoData } from "@/utils/cartStorage";
import { unescapeProductDisplayText } from "@/utils/general";
import { getDetailsByPathname } from "@/utils/getDetailsByPathname";
export const HASH = (input: string) => sha256(input);
const DEVMODE = process.env.NEXT_PUBLIC_DEVMODE === "1";
const MAX_PAYLOAD_BYTES = 16384;
const DEFAULT_PAGE_TITLE = "Nutrafol";
const PAGE_TITLE_RETRY_MS = 250;

const getDocumentTitle = () =>
  typeof document !== "undefined" ? document.title?.split(" | ")[0].trim() : DEFAULT_PAGE_TITLE;
/*
 * GA4 requires Numbers for these specific fields
 */
const numericFields = [
  "price",
  "value",
  "tax",
  "shipping",
  "quantity",
  "discount",
  "index",
  "order_count",
];

/**
 * Single-pass transformation to align with AdSwerve GA4 requirements.
 * Handles: Numeric casting, striping empty values (undefined), and default injection.
 */
const formatPayload = (
  data: EventData,
  isProduct = false,
  formatNumericFields = true
): EventData => {
  const result: EventData = { ...data };
  const { page_type, product_line, business_line } = getDetailsByPathname(data);

  if (result?.product_gender === "Unisex") {
    result.product_gender = "All";
  }

  result.affiliation = data.affiliation || "Nutrafol";
  result.brand = data.brand || "Nutrafol";
  result.business_line = data.business_line ?? business_line ?? "Hair";
  result.page_type = data.page_type || page_type;
  result.product_line = data.product_line || product_line;
  result.path = data.path ?? (typeof window !== "undefined" ? window?.location?.pathname : "");

  if (result?.product_gender === "Unisex") {
    result.product_gender = "All";
  }

  if (typeof result.name === "string") {
    result.name = unescapeProductDisplayText(result.name);
  }

  if (typeof result.sku === "string" && result.sku.indexOf("-") === -1) {
    if (result?.shipping_interval) {
      result.sku += "-S01";
    } else {
      result.sku += "-N01";
    }
  }

  // 1. Inject Mandatory Global Parameters (Event-level only)
  if (!isProduct) {
    result.anonymousId = getSegmentAnonymousId();
    result.currency = data.currency || "USD";
    result.page_title = data.page_title || getDocumentTitle() || DEFAULT_PAGE_TITLE;
    result.page_url = data.page_url || (typeof window !== "undefined" ? window.location.href : "");
    result.userAgent =
      data.userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "");
  }

  (Object.keys(result) as Array<keyof EventData>).forEach((key) => {
    const value = result[key];

    if (
      numericFields.includes(key as string) &&
      formatNumericFields &&
      value !== undefined &&
      value !== ""
    ) {
      const num = Number(value);
      result[key] = isNaN(num) ? null : num;
    } else if (value === "" || value === undefined) {
      // AdSwerve spec: missing values MUST be passed as NULL, to be present in JSON
      result[key] = null;
    }
  });

  //Recursive Product Processing
  if (Array.isArray(result.products)) {
    result.products = result.products
      .filter((el) => el?.sku)
      .map((p, index) => {
        const formatted = formatPayload(p, true);
        formatted.index = index + 1;
        formatted.product_index = index + 1;
        return formatted;
      });
  }

  return result;
};

/*
 * After a client-side navigation Next updates document.title after mount effects run, so events
 * fired from those effects can see an empty title. Retry once, then formatPayload uses the default.
 */
export const segmentEvent = (
  eventName = "Marketing CTA Clicked",
  eventData: EventData,
  callback = () => {}
) => {
  if (typeof window !== "undefined" && !eventData.page_title && !getDocumentTitle()) {
    setTimeout(() => sendSegmentEvent(eventName, eventData, callback), PAGE_TITLE_RETRY_MS);
    return;
  }
  sendSegmentEvent(eventName, eventData, callback);
};

const sendSegmentEvent = (eventName: string, eventData: EventData, callback: () => void) => {
  const isCheckout = eventName === "Checkout Started";
  const formatNumericFields = !isCheckout;
  // NT2-30446: message_category is null for Marketing CTA Clicked outside of ribbons
  const isCtaWithoutCategory =
    eventName === "Marketing CTA Clicked" && eventData.message_category === undefined;
  const payload = formatPayload(
    isCtaWithoutCategory ? { ...eventData, message_category: null } : eventData,
    false,
    formatNumericFields
  );

  // 16KB Payload Warning
  if (DEVMODE) {
    const size = new Blob([JSON.stringify(payload)]).size;
    if (size > MAX_PAYLOAD_BYTES) {
      // eslint-disable-next-line no-console
      console.warn(
        `[GA4 WARNING] Payload for "${eventName}" is ${size} bytes (Max: 16KB). This call will fail silently.`
      );
    }
  }

  // --- logic-based business rules ---
  const lowerEvent = eventName.toLowerCase();

  // Meta Custom Events
  if (
    typeof window.fbq === "function" &&
    (lowerEvent.includes("clicked") || lowerEvent.includes("added"))
  ) {
    window.fbq("trackCustom", "Button Click");
  }

  // Segment Cookie Sync for Product Added
  if (eventName === "Product Added") {
    payload.event_id = +new Date();
    document.cookie = `segment_event_id=${payload.event_id};path=/;secure`;
    // Clear any stale messages while adding
    clearCartUndoData();
    clearRemovedItem();
  }

  // Type-safe Key Sorting
  const sortedPayload = (Object.keys(payload) as Array<keyof EventData>)
    .sort()
    .reduce<Partial<EventData>>((accumulator, key) => {
      (accumulator[key] as EventData[typeof key]) = payload[key];
      return accumulator;
    }, {});

  /*
   * Ensure conversion event names match GA4 key events where applicable
   * --- Final Segment Dispatch ---
   */
  try {
    // track() can return void or a Promise
    const result = window.analytics?.track(eventName, sortedPayload);

    const handleSuccess = () => {
      if (DEVMODE) {
        // eslint-disable-next-line no-console
        console.info(`Track Success: ${eventName}`, sortedPayload);
      }
      callback();
    };

    // Safely check if 'result' is a Promise (has a .then method)
    if (result && typeof result === "object" && "then" in result) {
      result.then(handleSuccess).catch((err: Error) => {
        // eslint-disable-next-line no-console
        console.error(`Async Track Error (${eventName}):`, err);
        callback();
      });
    } else {
      // If it's void, Segment has already queued the event; execute immediately
      handleSuccess();
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Sync Track Error (${eventName}):`, err);
    callback();
  }
};

export const identifyEvent = (
  identity: Record<string, string | boolean>,
  integrations?: Record<string, boolean>
) => {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.log("identifyEvent", identity, integrations);
  }
  for (const k in identity) {
    let val = identity[k];
    if (typeof val === "string") {
      if (k === "email") {
        identity["email_hashed"] = HASH(val);
        if (DEVMODE) {
          window.console.log("adding email_hashed", val, HASH(val));
        }
      }
      if (k === "telephone" || k === "phone") {
        if (!val.startsWith("+1")) {
          val = "+1" + val;
        }
        identity["phone_hashed"] = HASH(val);
        if (DEVMODE) {
          window.console.log("adding phone_hashed", val, HASH(val));
        }
      }
    }
  }
  const userId =
    typeof identity.userId === "string"
      ? identity.userId
      : typeof identity.email === "string"
        ? identity.email
        : "";

  if (integrations) {
    const traits = { ...identity, integrations };
    if (userId) {
      window.analytics?.identify?.(userId, traits);
    } else {
      window.analytics?.identify?.(traits);
    }
  } else if (userId) {
    window.analytics?.identify?.(userId, identity);
  } else {
    window.analytics?.identify?.(identity);
  }
};
